import { useCallback, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import ChevronUpIcon from "@atlaskit/icon/core/chevron-up";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import type {
    CollectionKey,
    CollectionPlan
} from "@/components/usage-based-pricing-calculator/src/context/types";
import {
    useHasCollectionSelected,
    useInputValues,
    useSetCollectionPlan
} from "@/components/usage-based-pricing-calculator/src/hooks/hooks";
import { wacToken } from "@/libraries/design/foundations";
import { CollectionServiceLogoBrandV3 } from "@/libraries/wpl/foundations/src/logos-v3/collection-service-v3-logo-brand";
import { CollectionSoftwareLogoBrandV3 } from "@/libraries/wpl/foundations/src/logos-v3/collection-software-v3-logo-brand";
import { CollectionStrategyLogoBrandV3 } from "@/libraries/wpl/foundations/src/logos-v3/collection-strategy-v3-logo-brand";
import { CollectionTeamworkLogoBrandV3 } from "@/libraries/wpl/foundations/src/logos-v3/collection-teamwork-v3-logo-brand";

import { useSolutionPlansTracking } from "../hooks/tracking";

import { messages } from "./solution-plans.messages";

const sectionStyles = css({
    backgroundColor: token("elevation.surface.sunken"),
    borderRadius: "12px",
    padding: token("space.400"),
    marginBlockStart: "40px"
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const subtitleStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.300")
});

const cardStyles = css({
    maxWidth: "327px",
    backgroundColor: wacToken("color.neutral.light0"),
    border: `2px solid ${wacToken("color.neutral.light300")}`,
    borderRadius: "20px",
    marginBlockEnd: token("space.200"),
    overflow: "hidden",
    transition: "border-color 0.2s"
});

const cardHeaderStyles = css({
    display: "flex",
    alignItems: "center",
    padding: `${token("space.200")} ${token("space.300")}`,
    cursor: "pointer",
    gap: token("space.200"),
    userSelect: "none",
    justifyContent: "space-between"
});

const cardHeaderContentStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.075"),
    flex: 1
});

const planTierStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    lineHeight: "16px",
    color: wacToken("color.primary.black"),
    fontWeight: 400
});

const productsTextStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    lineHeight: "16px",
    color: wacToken("color.neutral.light800")
});

const cardBodyStyles = css({
    borderBlockStart: `1px solid ${wacToken("color.neutral.light300")}`
});

const planRowStyles = css({
    "display": "flex",
    "alignItems": "center",
    "padding": `${token("space.150")} ${token("space.300")}`,
    "gap": token("space.200"),
    "cursor": "pointer",
    "&:hover": {
        backgroundColor: wacToken("color.neutral.light100")
    }
});

const planRowSelectedStyles = css({
    backgroundColor: wacToken("color.neutral.light100")
});

const planRowDividerStyles = css({
    borderBlockStart: `1px solid ${wacToken("color.neutral.light200")}`
});

const planRowContentStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flex: 1
});

const PLAN_VALUES: CollectionPlan[] = [
    "free",
    "standard",
    "premium",
    "enterprise"
];

const cardHeaderMainStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100")
});

const solutionPlansContainerStyles = css({
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: token("space.100"),
    alignItems: "start",

    [media.below.md]: {
        gridTemplateColumns: "repeat(1, 1fr)"
    }
});

const PLAN_MESSAGE_KEYS: Record<CollectionPlan, keyof typeof messages> = {
    free: "planFree",
    standard: "planStandard",
    premium: "planPremium",
    enterprise: "planEnterprise"
};

const COLLECTION_MESSAGE_KEYS: Record<CollectionKey, keyof typeof messages> = {
    teamwork: "teamworkCollection",
    strategy: "strategyCollection",
    service: "serviceCollection",
    software: "softwareCollection"
};

const COLLECTION_LOGO_COMPONENTS: Record<CollectionKey, React.ComponentType> = {
    teamwork: CollectionTeamworkLogoBrandV3,
    strategy: CollectionStrategyLogoBrandV3,
    service: CollectionServiceLogoBrandV3,
    software: CollectionSoftwareLogoBrandV3
};

const COLLECTION_BORDER_COLORS: Record<CollectionKey, string> = {
    teamwork: wacToken("color.primary.blue"),
    strategy: wacToken("color.primary.saffron"),
    service: wacToken("color.extended.saffron300"),
    software: wacToken("color.extended.lime600")
};

// Products included per collection per plan
const COLLECTION_PRODUCTS: Record<
    CollectionKey,
    Record<CollectionPlan, string[]>
> = {
    teamwork: {
        free: ["Jira", "Confluence", "Loom", "Rovo"],
        standard: ["Jira", "Confluence", "Loom", "Rovo"],
        premium: ["Jira", "Confluence", "Loom", "Rovo"],
        enterprise: ["Jira", "Confluence", "Loom", "Rovo"]
    },
    strategy: {
        free: ["Focus", "Talent", "Align", "Rovo"],
        standard: ["Focus", "Talent", "Align", "Rovo"],
        premium: ["Focus", "Talent", "Align", "Rovo"],
        enterprise: ["Focus", "Talent", "Align", "Rovo"]
    },
    service: {
        free: [
            "Jira Service Management",
            "Customer Service Management",
            "Assets",
            "Rovo"
        ],
        standard: [
            "Jira Service Management",
            "Customer Service Management",
            "Assets",
            "Rovo"
        ],
        premium: [
            "Jira Service Management",
            "Customer Service Management",
            "Assets",
            "Rovo"
        ],
        enterprise: [
            "Jira Service Management",
            "Customer Service Management",
            "Assets",
            "Rovo"
        ]
    },
    software: {
        free: ["Rovo Dev", "DX", "Pipelines", "Bitbucket", "Compass"],
        standard: ["Rovo Dev", "DX", "Pipelines", "Bitbucket", "Compass"],
        premium: ["Rovo Dev", "DX", "Pipelines", "Bitbucket", "Compass"],
        enterprise: ["Rovo Dev", "DX", "Pipelines", "Bitbucket", "Compass"]
    }
};

const ProductDescriptionCard = ({
    collectionKey,
    plan
}: {
    collectionKey: CollectionKey;
    plan: CollectionPlan;
}) => {
    const intl = useIntl();
    const products = COLLECTION_PRODUCTS[collectionKey][plan];
    const planLabel = intl.formatMessage(messages[PLAN_MESSAGE_KEYS[plan]]);

    return (
        <div css={[planRowContentStyles]}>
            <span css={planTierStyles}>{planLabel}</span>
            <span css={productsTextStyles}>{products.join(" • ")}</span>
        </div>
    );
};

const PlanCard = ({
    collectionKey,
    plan,
    onPlanChange
}: {
    collectionKey: CollectionKey;
    plan: CollectionPlan;
    onPlanChange: (key: CollectionKey, plan: CollectionPlan) => void;
}) => {
    const intl = useIntl();
    const [expanded, setExpanded] = useState(false);
    const collectionName = intl.formatMessage(
        messages[COLLECTION_MESSAGE_KEYS[collectionKey]]
    );
    const CollectionLogo = COLLECTION_LOGO_COMPONENTS[collectionKey];
    const products = COLLECTION_PRODUCTS[collectionKey][plan];
    const planLabel = intl.formatMessage(messages[PLAN_MESSAGE_KEYS[plan]]);

    return (
        <div
            css={[
                cardStyles,
                css({
                    border: `2px solid ${COLLECTION_BORDER_COLORS[collectionKey]}`
                })
            ]}
        >
            {/* Card header - collapsed view */}
            <div
                css={cardHeaderStyles}
                onClick={() => setExpanded(!expanded)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded(!expanded);
                    }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-label={`${collectionName} - ${planLabel}`}
            >
                <div css={cardHeaderMainStyles}>
                    <CollectionLogo />
                    <div css={cardHeaderContentStyles}>
                        <span css={planTierStyles}>{planLabel}</span>
                        <span css={productsTextStyles}>
                            {products.join(" • ")}
                        </span>
                    </div>
                </div>
                {expanded ? (
                    <ChevronUpIcon label="" size="small" />
                ) : (
                    <ChevronDownIcon label="" size="small" />
                )}
            </div>

            {/* Expanded plan selector rows */}
            {expanded && (
                <div css={cardBodyStyles}>
                    {PLAN_VALUES.map((planValue, index) => {
                        const isSelected = plan === planValue;
                        return (
                            <div
                                key={planValue}
                                css={[
                                    planRowStyles,
                                    isSelected && planRowSelectedStyles,
                                    index > 0 && planRowDividerStyles
                                ]}
                                role="button"
                                tabIndex={0}
                                aria-pressed={isSelected}
                                onClick={() =>
                                    onPlanChange(collectionKey, planValue)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onPlanChange(collectionKey, planValue);
                                    }
                                }}
                            >
                                <div css={cardHeaderMainStyles}>
                                    <CollectionLogo />
                                    <ProductDescriptionCard
                                        collectionKey={collectionKey}
                                        plan={planValue}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const SolutionPlans = () => {
    const { collections } = useInputValues();
    const setCollectionPlan = useSetCollectionPlan();
    const { firePlanChangedEvent } = useSolutionPlansTracking();
    const hasCollectionSelected = useHasCollectionSelected();

    const selectedCollections = (
        Object.keys(collections) as CollectionKey[]
    ).filter((key) => collections[key].selected);

    const handlePlanChange = useCallback(
        (collectionKey: CollectionKey, plan: CollectionPlan) => {
            setCollectionPlan(collectionKey, plan);
            firePlanChangedEvent(collectionKey, plan);
        },
        [setCollectionPlan, firePlanChangedEvent]
    );

    return (
        <div css={sectionStyles}>
            <Heading size="medium" as="h2">
                <span css={titleStyles}>
                    <FormattedMessage {...messages.solutionPlansTitle} />
                </span>
            </Heading>
            <p css={subtitleStyles}>
                <FormattedMessage
                    {...(hasCollectionSelected
                        ? messages.solutionPlansSubtitle
                        : messages.solutionPlansEmptyState)}
                />
            </p>
            <div css={solutionPlansContainerStyles}>
                {hasCollectionSelected &&
                    selectedCollections.map((collectionKey) => (
                        <PlanCard
                            key={collectionKey}
                            collectionKey={collectionKey}
                            plan={collections[collectionKey].plan}
                            onPlanChange={handlePlanChange}
                        />
                    ))}
            </div>
        </div>
    );
};
